import { Dispatch, SetStateAction, useState } from 'react';
import styles from './Dropzone.module.scss';
import { Loader } from '@mantine/core';
import { Dropzone, FileWithPath } from '@mantine/dropzone';
import { useDictionary } from '@/src/dictionaries/dictionary-provider';

export const DropzoneCloudinary = ({
  setImageUrl,
  imageUrl,
  error,
}: {
  setImageUrl: Dispatch<SetStateAction<string | null>>;
  imageUrl: string | null;
  error?: string;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const dictionary = useDictionary();

  // ✅ Handle image drop & compress before upload
  const handleDrop = async (acceptedFiles: FileWithPath[]) => {
    setIsLoading(true);
    const file = acceptedFiles[0];

    try {
      console.log('📸 Compressing image before upload...');
      const compressedFile = await compressImage(file, 800, 75); // Resize to 800px width, 75% quality
      console.log('✅ Compression complete. Uploading...');

      const formData = new FormData();
      formData.append('file', compressedFile);

      const response = await fetch('/api/upload', {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        const { url } = await response.json();
        setImageUrl(url); // ✅ Set Cloudinary URL for database
        console.log(`✅ Image uploaded: ${url}`);
      } else {
        console.error('❌ Failed to upload image');
        alert("Le fichier n'est pas sauvegardé. Veuillez réessayer");
      }
    } catch (error) {
      console.error('❌ Error uploading image:', error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Dropzone
        onDrop={handleDrop}
        accept={['image/*']}
        multiple={false}
        style={{
          border: `2px dashed ${error ? 'red' : 'black'}`,
          borderRadius: '10px',
          padding: '20px',
          marginBottom: `${error ? '0' : '20px'}`,
        }}
      >
        <div style={{ textAlign: 'center' }}>
          {!imageUrl && isLoading ? (
            <Loader />
          ) : !imageUrl ? (
            <p>{dictionary.general.dropzone}</p>
          ) : (
            <img
              src={imageUrl}
              alt="Uploaded Image"
              style={{ maxWidth: '100%', maxHeight: '200px', objectFit: 'cover' }}
            />
          )}
        </div>
      </Dropzone>
      {error && <p className={styles.dropzone}>{error}</p>}
    </>
  );
};

/**
 * ✅ Compress Image using Canvas before Upload
 * - Resizes image while keeping aspect ratio
 * - Converts image to JPEG with 75% quality
 */
async function compressImage(file: File, maxWidth: number, quality: number): Promise<File> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target?.result as string;

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // ✅ Calculate new width while maintaining aspect ratio
        const scaleFactor = maxWidth / img.width;
        canvas.width = maxWidth;
        canvas.height = img.height * scaleFactor;

        // ✅ Draw resized image on canvas
        ctx?.drawImage(img, 0, 0, canvas.width, canvas.height);

        // ✅ Convert canvas to compressed file
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              return reject(new Error('Compression failed'));
            }
            const compressedFile = new File([blob], file.name, { type: 'image/jpeg' });
            resolve(compressedFile);
          },
          'image/jpeg',
          quality / 100 // Convert quality to range 0-1
        );
      };
    };

    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}
